import { apiFetch, hasLiveApi } from '@/api/client'
import {
  MY_PNL_PAGE_SIZE,
  mapDrillRow,
  mapMyPnlEventOptions,
  mapMyPnlList,
  myPnlDrillRequestBody,
  myPnlRequestBody,
  unwrapMyPnlDrillRows,
  type MyPnlDrillResponse,
  type MyPnlEventOption,
  type MyPnlEventRow,
  type MyPnlFilter,
  type MyPnlListResponse,
  type MyPnlSum,
} from '@/api/map/myPnl'

export {
  MY_PNL_EVENT_NAME_PREFIX,
  MY_PNL_PAGE_SIZE,
  filterMyPnlRowsForEvent,
  injectMyPnlDrillRows,
  mapMyPnlDrillAsEventRow,
  mergeMyPnlEventOptions,
  myPnlSortTarget,
  type MyPnlEventOption,
  type MyPnlEventRow,
  type MyPnlFilter,
  type MyPnlSum,
} from '@/api/map/myPnl'

type MyPnlNewListResponse = MyPnlListResponse & {
  data: MyPnlListResponse['data'] & { totalRecords?: number }
}

const EMPTY_LIST = {
  rows: [] as MyPnlEventRow[],
  total: 0,
  pageSum: {
    win: 0,
    commission: 0,
    netAmt: 0,
    winINR: 0,
    commissionINR: 0,
    netAmtINR: 0,
    betFairAmount: 0,
  } satisfies MyPnlSum,
  maxRow: MY_PNL_PAGE_SIZE,
}

function n(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const x = Number(v)
    if (Number.isFinite(x)) return x
  }
  return 0
}

/** Lab Aggregate My P/L New — `POST /v1/account/getPLBySubGameForAggregatorBq`. */
export async function fetchMyPnlNewList(filter: MyPnlFilter) {
  if (!hasLiveApi()) return { ...EMPTY_LIST }
  const response = await apiFetch<MyPnlNewListResponse>(
    '/v1/account/getPLBySubGameForAggregatorBq',
    {
      method: 'POST',
      body: JSON.stringify(myPnlRequestBody(filter)),
    },
  )
  const mapped = mapMyPnlList(response)
  const totalRecords = n(response.data?.totalRecords)
  if (totalRecords > 0) return { ...mapped, total: totalRecords }
  return mapped
}

/** Lab New event picker — `POST /v1/account/getEventNameAndIdBq`. */
export async function fetchMyPnlNewEventOptions(filter: MyPnlFilter): Promise<MyPnlEventOption[]> {
  if (!hasLiveApi()) return [{ value: 'All', label: 'All' }]
  const body = myPnlRequestBody({ ...filter, page: 1 })
  delete body.page
  delete body.bookmakerSessionFlag
  delete body.typeName
  delete body.eventId
  delete body.eventName
  const response = await apiFetch<{
    status?: number
    value?: boolean
    data: {
      results: Array<{
        EVENTID?: string
        EVENTNAME?: string
        HAS_EVENT_ID?: string | number
      }>
    }
  }>('/v1/account/getEventNameAndIdBq', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  return mapMyPnlEventOptions(response)
}

export async function fetchMyPnlNewMarkets(
  filter: MyPnlFilter,
  row: Pick<MyPnlEventRow, 'id' | 'eventId' | 'eventName' | 'subGame'>,
) {
  if (!hasLiveApi()) return []
  const response = await apiFetch<MyPnlDrillResponse>(
    '/v1/account/getMarketWithinEventAggregatorBq',
    {
      method: 'POST',
      body: JSON.stringify(myPnlDrillRequestBody(filter, row)),
    },
  )
  return unwrapMyPnlDrillRows(response).map(mapDrillRow)
}

export async function fetchMyPnlNewUsers(
  filter: MyPnlFilter,
  row: Pick<MyPnlEventRow, 'id' | 'eventId' | 'eventName' | 'subGame'>,
) {
  if (!hasLiveApi()) return []
  const response = await apiFetch<MyPnlDrillResponse>(
    '/v1/account/getUsersWithinEventAggregatorBq',
    {
      method: 'POST',
      body: JSON.stringify(myPnlDrillRequestBody(filter, row)),
    },
  )
  return unwrapMyPnlDrillRows(response).map(mapDrillRow)
}
